import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, AlertCircle, Building2, CheckCircle2, Hash, History, Info, Lock, Save } from "lucide-react";
import { toast } from "react-toastify";
import { apiClient } from "@/services/apiClient";
import { profileService, LegalEntityHierarchy } from "@/services/profileService";
import {
  crmIntegrationConfigsService,
  type CrmIntegrationConfigRecord,
} from "@/services/crmIntegrationConfigsService";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Tooltip } from "@/components/atoms/Tooltip";
import {
  fetchBranchEntityStatuses,
  aggregateBranchStatus,
  REAPIT_HISTORICAL_SYNC_ENTITY_TYPES,
} from "@/services/crmGatewaySyncService";
import { HistoricalSyncModal } from "./HistoricalSyncModal";

const CRM_ID = "REAPIT";
const CRM_LABEL = "Reapit";
const CONFIG_LEVEL = "LEGAL_ENTITY";
const ACTIVE_HISTORICAL_SYNC_STATUSES = new Set(["IN_PROGRESS", "RUNNING", "QUEUED"]);
const LOCKED_HINT = "The Reapit customer code can't be changed once connected";

interface Props {
  onBack: () => void;
}

/**
 * Reapit configuration. Like Propco there is no hierarchy to map: a single CRM
 * integration config row on the ROOT legal entity covers the whole group, and
 * the only credential is the customer code.
 */
export const ReapitIntegrationDetail: React.FC<Props> = ({ onBack }) => {
  const { keycloak } = useKeycloak();
  const email = keycloak?.tokenParsed?.email as string | undefined;

  const [rootEntity, setRootEntity] = useState<LegalEntityHierarchy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [customerCode, setCustomerCode] = useState("");
  const [savedCustomerCode, setSavedCustomerCode] = useState("");
  const [hasSavedConfig, setHasSavedConfig] = useState(false);

  const [historicalStatus, setHistoricalStatus] = useState<string | undefined>();
  const [syncModalOpen, setSyncModalOpen] = useState(false);

  const connected = Boolean(hasSavedConfig && savedCustomerCode.trim());
  const locked = connected;

  const hydrate = (le: LegalEntityHierarchy, config?: CrmIntegrationConfigRecord | null) => {
    setRootEntity(le);
    const next = config?.customerCode ?? "";
    setHasSavedConfig(Boolean(config));
    setCustomerCode(next);
    setSavedCustomerCode(next);
  };

  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (successTimer.current) clearTimeout(successTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    setLoading(true);
    void profileService
      .get(email)
      .then(async (p) => {
        const legalEntities = Array.isArray(p?.legalEntities) ? p.legalEntities : [];
        const first = legalEntities[0];
        if (!first) throw new Error("No legal entity is available for this account.");
        const rootId = await resolveRootLegalEntityId(first.id);
        const root = findEntity(legalEntities, rootId) ?? first;
        const config = await loadReapitConfig(root.id);
        if (cancelled) return;
        hydrate(root, config);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load the Reapit configuration.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  useEffect(() => {
    if (!connected || !rootEntity) return;
    let cancelled = false;
    void fetchBranchEntityStatuses([rootEntity.id], {
      entityTypes: REAPIT_HISTORICAL_SYNC_ENTITY_TYPES,
      scope: "LEGAL_ENTITY",
    })
      .then((byEntity) => {
        if (cancelled) return;
        setHistoricalStatus(
          aggregateBranchStatus(byEntity[rootEntity.id] ?? {}, REAPIT_HISTORICAL_SYNC_ENTITY_TYPES),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [connected, rootEntity]);

  const historicalSyncRunning = ACTIVE_HISTORICAL_SYNC_STATUSES.has(
    String(historicalStatus ?? "").toUpperCase(),
  );

  const codeMissing = !customerCode.trim();
  const isDirty = customerCode !== savedCustomerCode;
  const saveDisabled = locked || !rootEntity || codeMissing || !isDirty;

  const saveReason = locked
    ? LOCKED_HINT
    : !rootEntity
      ? "Loading your legal entity"
      : codeMissing
        ? "Enter the Reapit customer code"
        : !isDirty
          ? "No changes to save"
          : "";

  async function handleSave() {
    if (!rootEntity || !email || saveDisabled) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await crmIntegrationConfigsService.bulkUpsert([
        {
          legalEntityId: rootEntity.id,
          branchId: null,
          configLevel: CONFIG_LEVEL,
          crmType: CRM_ID,
          agencyRef: null,
          externalBranchId: null,
          username: null,
          password: null,
          customerCode: customerCode.trim(),
          isActive: true,
          pollingEnabled: false,
          pollingIntervalMinutes: null,
          pageSize: null,
          rateLimitPerMinute: null,
          concurrentRequests: null,
        },
      ]);

      useCrmMappingSyncStore.getState().bumpCrmMappingEpoch();

      const saved = await loadReapitConfig(rootEntity.id);
      if (saved) {
        hydrate(rootEntity, saved);
      } else {
        setHasSavedConfig(true);
        setSavedCustomerCode(customerCode.trim());
      }

      setSuccess(true);
      if (successTimer.current) clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSuccess(false), 3000);
      toast.success("Reapit customer code saved.");
    } catch (err: unknown) {
      const res = err as { response?: { data?: { message?: string } }; message?: string };
      setError(res?.response?.data?.message ?? res?.message ?? "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bridge-accent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-bridge-bg">
      <header className="px-8 py-4 bg-white border-b border-bridge-border flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-bridge-bg rounded-full text-bridge-muted transition-colors"
            aria-label="Back to integrations"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="text-2xl">🏠</div>
            <div>
              <h1 className="text-xl font-bold text-bridge-fg">{CRM_LABEL} Configuration</h1>
              <p className="text-xs text-bridge-muted mt-0.5">
                One {CRM_LABEL} customer code connects every legal entity and branch.
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={saveDisabled}
          title={saveReason || undefined}
          className="bg-bridge-accent hover:bg-bridge-accent/90 text-white rounded-xl px-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Configuration
        </Button>
      </header>

      <main className="flex-1 overflow-auto p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-2xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 bg-green-50 border border-green-100 text-green-600 text-sm rounded-2xl flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              Configuration saved successfully!
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 text-blue-800 text-sm">
            <Info className="w-5 h-5 shrink-0 text-blue-400" />
            <div className="space-y-1">
              <p>
                Enter your Reapit <strong>Customer Code</strong>. There are no per-branch references
                to map — one code covers the whole group, so it is saved on your root legal entity.
              </p>
              <p>Once saved, every legal entity and branch beneath it is linked to {CRM_LABEL}.</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-bridge-border shadow-sm overflow-hidden">
            <div className="px-8 py-4 bg-slate-50 border-b border-bridge-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-bridge-accent" />
                <span className="font-bold text-bridge-fg">{rootEntity?.name ?? "Root Legal Entity"}</span>
              </div>
              {connected ? (
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-bold border border-green-100">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Connected
                  </span>
                  <Tooltip
                    side="left"
                    content={
                      historicalSyncRunning
                        ? "Syncing in progress"
                        : "Sync historical data for this legal entity"
                    }
                  >
                    <span className="inline-flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => setSyncModalOpen(true)}
                        disabled={historicalSyncRunning}
                        aria-label={`Sync historical data for ${rootEntity?.name ?? "root legal entity"}`}
                        className={`w-9 h-9 inline-flex items-center justify-center rounded-full border border-bridge-border bg-white text-bridge-accent transition-colors ${
                          historicalSyncRunning
                            ? "opacity-40 cursor-not-allowed"
                            : "hover:bg-bridge-accent hover:text-white hover:border-bridge-accent"
                        }`}
                      >
                        <History className="w-4 h-4" />
                      </button>
                    </span>
                  </Tooltip>
                </div>
              ) : (
                <span className="px-2.5 py-1 rounded-full bg-slate-200/80 text-[10px] font-bold uppercase tracking-wide text-slate-500 border border-slate-200">
                  Not connected
                </span>
              )}
            </div>

            <div className="px-8 py-7 space-y-6" title={locked ? LOCKED_HINT : undefined}>
              <div className="flex items-center justify-between gap-6">
                <label
                  htmlFor="reapit-customer-code"
                  className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider whitespace-nowrap shrink-0"
                >
                  Customer Code <span className="text-red-500">*</span>
                </label>
                <Input
                  id="reapit-customer-code"
                  autoComplete="off"
                  leading={<Hash className="w-4 h-4" />}
                  placeholder="Reapit customer code"
                  className={lockedFieldClass(locked)}
                  title={locked ? LOCKED_HINT : undefined}
                  readOnly={locked}
                  aria-readonly={locked || undefined}
                  value={customerCode}
                  onChange={(e) => setCustomerCode(e.target.value)}
                  trailing={locked ? <Lock className="w-3.5 h-3.5" /> : undefined}
                />
              </div>

              {saveReason && <p className="pt-1 text-[11px] text-bridge-muted">{saveReason}.</p>}
            </div>
          </div>
        </div>
      </main>

      <HistoricalSyncModal
        open={syncModalOpen}
        branchId={rootEntity?.id ?? ""}
        branchName={rootEntity?.name ?? "Root Legal Entity"}
        entityTypes={REAPIT_HISTORICAL_SYNC_ENTITY_TYPES}
        scope="LEGAL_ENTITY"
        targetLabel="Legal Entity"
        status={historicalStatus}
        onStatusChange={(_id, status) => setHistoricalStatus(status.toUpperCase())}
        onClose={() => setSyncModalOpen(false)}
      />
    </div>
  );
};

async function resolveRootLegalEntityId(legalEntityId: string): Promise<string> {
  try {
    const res = await apiClient.get<{ rootLegalEntityId?: string }>(
      "/profile/hierarchy/root-legal-entity",
      { params: { legalEntityId } },
    );
    return res.data?.rootLegalEntityId?.trim() || legalEntityId;
  } catch {
    return legalEntityId;
  }
}

function lockedFieldClass(locked: boolean): string {
  const base = "h-10 w-3/4 shrink-0 shadow-sm";
  return locked
    ? `${base} bg-slate-50 border-bridge-border/70 text-bridge-muted cursor-not-allowed focus-within:border-bridge-border/70 focus-within:shadow-sm [&_input]:cursor-not-allowed [&_input]:text-bridge-muted`
    : `${base} bg-white`;
}

async function loadReapitConfig(legalEntityId: string): Promise<CrmIntegrationConfigRecord | null> {
  try {
    const configs = await crmIntegrationConfigsService.list(legalEntityId);
    return configs.find((c) => c.crmType?.toUpperCase() === CRM_ID) ?? null;
  } catch {
    return null;
  }
}

function findEntity(entities: LegalEntityHierarchy[], id: string): LegalEntityHierarchy | null {
  for (const entity of entities) {
    if (entity.id === id) return entity;
    if (entity.subEntities) {
      const found = findEntity(entity.subEntities, id);
      if (found) return found;
    }
  }
  return null;
}
